import React, { useState, useEffect, useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import Loader from "../components/Loader/Loader";
import Toolbar from "./Toolbar/Toolbar";
import NavigationPane from "./NavigationPane/NavigationPane";
import BreadCrumb from "./BreadCrumb/BreadCrumb";
import FileList from "./FileList/FileList";
import Actions from "./Actions/Actions";

import { FilesProvider } from "../contexts/FilesContext";
import { FileNavigationProvider } from "../contexts/FileNavigationContext";
import { SelectionProvider } from "../contexts/SelectionContext";
import { ClipBoardProvider } from "../contexts/ClipboardContext";
import { LayoutProvider } from "../contexts/LayoutContext";
import { useTriggerAction } from "../hooks/useTriggerAction";
import { useColumnResize } from "../hooks/useColumnResize";
import {
  dateStringValidator,
  urlValidator,
} from "../validators/propValidators";
import { TranslationProvider } from "../contexts/TranslationProvider";
import "./FileManager.scss";

const FileManager = ({
  files,
  fileUploadConfig,
  isLoading,
  onCreateFolder,
  onFileUploading = () => {},
  onFileUploaded = () => {},
  onCut,
  onCopy,
  onPaste,
  onRename,
  onDownload,
  onDelete = () => null,
  onLayoutChange = () => {},
  onRefresh,
  onFileOpen = () => {},
  onSelect,
  onError = () => {},
  layout = "grid",
  enableFilePreview = true,
  maxFileSize,
  filePreviewPath,
  acceptedFileTypes,
  height = "600px",
  width = "100%",
  initialPath = "",
  filePreviewComponent,
  primaryColor = "#212631",
  fontFamily = "Nunito Sans, sans-serif",
  language = "ru",
  allowCreateFolder = true,
  allowUploadFile = true,
  allowCutCopy = true,
  allowDelete = true, // НОВЫЙ ПРОПС
}) => {
  const triggerAction = useTriggerAction();
  const {
    containerRef,
    colSizes,
    isDragging,
    handleMouseMove,
    handleMouseUp,
    handleMouseDown,
  } = useColumnResize(20, 80);
  
  const customStyles = {
    "--file-manager-font-family": fontFamily,
    "--file-manager-primary-color": primaryColor,
    height,
    width,
  };

  // Обработчик создания папки
  const handleCreateFolder = useCallback((path = "/") => {
    if (onCreateFolder) {
      onCreateFolder(path);
    } else {
      console.warn("onCreateFolder callback not provided");
    }
  }, [onCreateFolder]);

  // Обработчик нажатия Delete на клавиатуре
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Игнорируем нажатие, если фокус в поле ввода
      if (['INPUT', 'TEXTAREA', 'SELECT'].includes(e.target.tagName)) {
        return;
      }

      if ((e.key === 'Delete' || e.key === 'Backspace') && allowDelete) {
        e.preventDefault();
        // ИСПРАВЛЕНО: trigger -> show
        triggerAction.show("delete");
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [allowDelete, triggerAction]);

  return (
    <main
      className="file-explorer"
      onContextMenu={(e) => e.preventDefault()}
      style={customStyles}
    >
      <ToastContainer />
      <Loader loading={isLoading} />
      <TranslationProvider language={language}>
        <FilesProvider filesData={files} onError={onError}>
          <FileNavigationProvider initialPath={initialPath}>
            <SelectionProvider onDownload={onDownload} onSelect={onSelect}>
              <ClipBoardProvider
                onPaste={onPaste}
                onCut={onCut}
                onCopy={onCopy}
              >
                <LayoutProvider layout={layout}>
                  <Toolbar
                    allowCreateFolder={allowCreateFolder}
                    allowUploadFile={allowUploadFile}
                    allowCutCopy={allowCutCopy}
                    allowDelete={allowDelete} // ПЕРЕДАЕМ В TOOLBAR
                    onLayoutChange={onLayoutChange}
                    onRefresh={onRefresh}
                    onCreateFolder={handleCreateFolder}
                    triggerAction={triggerAction}
                  />
                  <section
                    ref={containerRef}
                    onMouseMove={handleMouseMove}
                    onMouseUp={handleMouseUp}
                    className="files-container"
                  >
                    <div
                      className="navigation-pane"
                      style={{ width: colSizes.col1 + "%" }}
                    >
                      <NavigationPane onFileOpen={onFileOpen} />
                      <div
                        className={`sidebar-resize ${
                          isDragging ? "sidebar-dragging" : ""
                        }`}
                        onMouseDown={handleMouseDown}
                      />
                    </div>

                    <div
                      className="folders-preview"
                      style={{ width: colSizes.col2 + "%" }}
                    >
                      <BreadCrumb />
                      <FileList
                        onCreateFolder={onCreateFolder}
                        onRename={onRename}
                        onFileOpen={onFileOpen}
                        onRefresh={onRefresh}
                        enableFilePreview={enableFilePreview}
                        triggerAction={triggerAction}
                        allowCutCopy={allowCutCopy}
                        allowCreateFolder={allowCreateFolder}
                        allowDelete={allowDelete} // ПЕРЕДАЕМ В FILELIST
                      />
                    </div>
                  </section>

                  <Actions
                    fileUploadConfig={fileUploadConfig}
                    onFileUploading={onFileUploading}
                    onFileUploaded={onFileUploaded}
                    onDelete={onDelete}
                    onRefresh={onRefresh}
                    maxFileSize={maxFileSize}
                    filePreviewPath={filePreviewPath}
                    filePreviewComponent={filePreviewComponent}
                    acceptedFileTypes={acceptedFileTypes}
                    triggerAction={triggerAction}
                    allowDelete={allowDelete} // ПЕРЕДАЕМ В ACTIONS
                  />
                </LayoutProvider>
              </ClipBoardProvider>
            </SelectionProvider>
          </FileNavigationProvider>
        </FilesProvider>
      </TranslationProvider>
    </main>
  );
};

FileManager.displayName = "FileManager";

FileManager.propTypes = {
  files: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      isDirectory: PropTypes.bool.isRequired,
      path: PropTypes.string.isRequired,
      updatedAt: dateStringValidator,
      size: PropTypes.number,
    })
  ).isRequired,
  fileUploadConfig: PropTypes.shape({
    url: urlValidator,
    headers: PropTypes.objectOf(PropTypes.string),
    method: PropTypes.oneOf(["POST", "PUT"]),
  }),
  isLoading: PropTypes.bool,
  onCreateFolder: PropTypes.func,
  onFileUploading: PropTypes.func,
  onFileUploaded: PropTypes.func,
  onRename: PropTypes.func,
  onDelete: PropTypes.func,
  onCut: PropTypes.func,
  onCopy: PropTypes.func,
  onPaste: PropTypes.func,
  onDownload: PropTypes.func,
  onLayoutChange: PropTypes.func,
  onRefresh: PropTypes.func,
  onFileOpen: PropTypes.func,
  onSelect: PropTypes.func,
  onError: PropTypes.func,
  layout: PropTypes.oneOf(["grid", "list"]),
  maxFileSize: PropTypes.number,
  enableFilePreview: PropTypes.bool,
  filePreviewPath: urlValidator,
  acceptedFileTypes: PropTypes.string,
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  initialPath: PropTypes.string,
  filePreviewComponent: PropTypes.func,
  primaryColor: PropTypes.string,
  fontFamily: PropTypes.string,
  language: PropTypes.string,
  allowCreateFolder: PropTypes.bool,
  allowUploadFile: PropTypes.bool,
  allowCutCopy: PropTypes.bool,
  allowDelete: PropTypes.bool, // ДОБАВЛЕН ПРОПС
};

export default FileManager;